/**
 * Map course live session plan drafts onto Day Wise schedule.
 *
 * LCM (mandatory): mark the learning day where that session's topics complete
 *   (same day — no extra calendar day).
 * Workshops: insert Online/Onsite Workshop day type(s) after the last
 *   overlapping topic day (dedicated workshop days).
 */

import {
  DayTypeMap,
  LIVE_SESSION_TOPIC,
  ONLINE_WORKSHOP_TOPIC,
  ONSITE_WORKSHOP_TOPIC,
  ScheduleData,
  ScheduleDay,
  Topic,
  isSpecialScheduleDay,
} from './scheduleUtils';

/** Default workshop length on the day-wise plan (matches course-plan DEFAULT_WORKSHOP_DAYS). */
export const DEFAULT_WORKSHOP_DAY_COUNT = 4;

export type CoursePlanSessionTopic = {
  id?: string;
  name?: string;
  subtopics?: { id?: string; name?: string }[];
};

export type CoursePlanSession = {
  category?: string;
  sequence?: number;
  title?: string;
  session_key?: string;
  sessionKey?: string;
  topics?: CoursePlanSessionTopic[];
  workshop_day_count?: number;
  workshopDayCount?: number;
};

export type SubjectLivePlanInput = {
  subjectName: string;
  sessions: CoursePlanSession[];
  courseTopics?: Topic[];
};

type PendingWorkshopInsert = {
  afterIdx: number;
  entry: string;
  count: number;
  order: number;
};

/** Collect subtopic ids from a planned session (plan + optional course topic fallback). */
export function collectPlanSessionSubtopicIds(
  session: CoursePlanSession,
  courseTopics?: Topic[],
): Set<string> {
  const ids = new Set<string>();
  for (const topic of session.topics || []) {
    for (const sub of topic.subtopics || []) {
      const sid = String(sub?.id || '').trim();
      if (sid) ids.add(sid);
    }
  }
  if (ids.size > 0 || !courseTopics?.length) return ids;

  const topicIdSet = new Set(
    (session.topics || [])
      .map((t) => String(t.id || '').trim())
      .filter(Boolean),
  );
  for (const courseTopic of courseTopics) {
    if (!topicIdSet.has(String(courseTopic.id))) continue;
    for (const sub of courseTopic.subtopics || []) {
      const sid = String(sub.id || '').trim();
      if (sid) ids.add(sid);
    }
  }
  return ids;
}

export function dayTypeEntryForPlanCategory(
  category: string,
): string | null {
  const c = String(category || '').trim();
  if (c === 'mandatory') return LIVE_SESSION_TOPIC;
  if (c === 'workshop_online') return ONLINE_WORKSHOP_TOPIC;
  if (c === 'workshop_onsite') return ONSITE_WORKSHOP_TOPIC;
  return null;
}

export function planHasLcmSessions(plans: SubjectLivePlanInput[]): boolean {
  return (plans || []).some((p) =>
    (p.sessions || []).some((s) => String(s.category || '') === 'mandatory'),
  );
}

function workshopDayCount(session: CoursePlanSession): number {
  const raw = session.workshop_day_count ?? session.workshopDayCount;
  const n = Number(raw);
  if (Number.isFinite(n) && n >= 1) return Math.floor(n);
  return DEFAULT_WORKSHOP_DAY_COUNT;
}

function dayOverlapsSubtopicIds(
  day: ScheduleDay,
  subtopicIds: Set<string>,
): boolean {
  if (!subtopicIds.size) return false;
  for (const sub of day.subtopicids || []) {
    if (subtopicIds.has(String(sub.subtopic_id || '').trim())) return true;
  }
  return false;
}

function learningDaysForSubject(
  learningSchedule: ScheduleData,
  subjectName: string,
): ScheduleDay[] {
  return (learningSchedule[subjectName] || []).filter(
    (d) => !isSpecialScheduleDay(d),
  );
}

function learningOffsetBeforeSubject(
  learningSchedule: ScheduleData,
  subjectName: string,
): number {
  const names = Object.keys(learningSchedule);
  const index = names.indexOf(subjectName);
  if (index <= 0) return 0;
  let offset = 0;
  for (let i = 0; i < index; i += 1) {
    offset += learningDaysForSubject(learningSchedule, names[i]).length;
  }
  return offset;
}

function lastOverlapLearningIndex(
  learningDays: ScheduleDay[],
  subtopicIds: Set<string>,
): number {
  let lastIdx = -1;
  learningDays.forEach((day, index) => {
    if (dayOverlapsSubtopicIds(day, subtopicIds)) lastIdx = index;
  });
  return lastIdx;
}

function sortPlanSessions(
  sessions: CoursePlanSession[],
): CoursePlanSession[] {
  return (sessions || []).slice().sort((a, b) => {
    const catRank = (c: string) => {
      if (c === 'mandatory') return 0;
      if (c === 'workshop_online') return 1;
      if (c === 'workshop_onsite') return 2;
      return 9;
    };
    const ca = catRank(String(a.category || ''));
    const cb = catRank(String(b.category || ''));
    if (ca !== cb) return ca - cb;
    return (Number(a.sequence) || 0) - (Number(b.sequence) || 0);
  });
}

/**
 * Workshop day types only (LCM uses same-day markers — see applyLcmMarkersFromCoursePlans).
 */
export function buildDayTypesFromCourseLivePlans(
  learningSchedule: ScheduleData,
  plans: SubjectLivePlanInput[],
): DayTypeMap {
  const pending: PendingWorkshopInsert[] = [];
  let orderCounter = 0;

  for (const plan of plans) {
    const { subjectName, sessions, courseTopics } = plan;
    const learningDays = learningDaysForSubject(learningSchedule, subjectName);
    if (!learningDays.length) continue;

    const offset = learningOffsetBeforeSubject(learningSchedule, subjectName);

    for (const session of sortPlanSessions(sessions)) {
      const category = String(session.category || '');
      if (category === 'mandatory') continue; // LCM → same-day marker, not a new day

      const entry = dayTypeEntryForPlanCategory(category);
      if (!entry) continue;

      const subtopicIds = collectPlanSessionSubtopicIds(session, courseTopics);
      if (!subtopicIds.size) continue;

      const lastIdx = lastOverlapLearningIndex(learningDays, subtopicIds);
      if (lastIdx < 0) continue;

      pending.push({
        afterIdx: offset + lastIdx,
        entry,
        count: workshopDayCount(session),
        order: orderCounter,
      });
      orderCounter += 1;
    }
  }

  pending.sort((a, b) => {
    if (a.afterIdx !== b.afterIdx) return a.afterIdx - b.afterIdx;
    return a.order - b.order;
  });

  const dayType: DayTypeMap = {};
  pending.forEach((item, processed) => {
    const insertsBefore = pending
      .slice(0, processed)
      .filter((p) => p.afterIdx <= item.afterIdx)
      .reduce((sum, p) => sum + p.count, 0);
    for (let i = 0; i < item.count; i += 1) {
      const dayNum = item.afterIdx + 1 + insertsBefore + 1 + i;
      dayType[`Day ${dayNum}`] = item.entry;
    }
  });

  return dayType;
}

/** @deprecated Prefer buildDayTypesFromCourseLivePlans for multi-subject. */
export function buildDayTypesFromCourseLivePlan(
  learningSchedule: ScheduleData,
  subjectName: string,
  sessions: CoursePlanSession[],
  courseTopics?: Topic[],
): DayTypeMap {
  return buildDayTypesFromCourseLivePlans(learningSchedule, [
    { subjectName, sessions, courseTopics },
  ]);
}

function stripLcmFields(day: ScheduleDay): ScheduleDay {
  const {
    live_session_lcm: _lcm,
    live_session_key: _key,
    live_session_title: _title,
    ...rest
  } = day;
  return rest;
}

/** Remove all LCM markers (batch opted out of course LCM). */
export function clearLcmMarkersFromSchedule(
  schedule: ScheduleData,
): ScheduleData {
  const next: ScheduleData = {};
  Object.keys(schedule).forEach((subjectName) => {
    next[subjectName] = (schedule[subjectName] || []).map((day) => {
      if (!day.live_session_lcm) return day;
      const cleared = stripLcmFields(day);
      // Keep subtopic_ids only on dedicated Live Session / workshop special rows
      if (!isSpecialScheduleDay(cleared)) {
        const { subtopic_ids: _ids, ...learning } = cleared;
        return learning;
      }
      return cleared;
    });
  });
  return next;
}

/** Clear LCM marker from one day (batch removed that session). */
export function clearLcmMarkerOnDay(
  schedule: ScheduleData,
  subjectName: string,
  dayLabel: string,
): ScheduleData {
  const days = schedule[subjectName];
  if (!days?.length) return schedule;
  return {
    ...schedule,
    [subjectName]: days.map((day) => {
      if (day.day !== dayLabel || !day.live_session_lcm) return day;
      const cleared = stripLcmFields(day);
      if (!isSpecialScheduleDay(cleared)) {
        const { subtopic_ids: _ids, ...learning } = cleared;
        return learning;
      }
      return cleared;
    }),
  };
}

/**
 * Mark a learning day with LCM (same day — does not insert a new day).
 * Used for manual Add LCM and for plan apply.
 */
export function markDayWithLcm(
  schedule: ScheduleData,
  subjectName: string,
  dayLabel: string,
  meta?: {
    title?: string;
    sessionKey?: string;
    subtopicIds?: string[];
  },
): ScheduleData {
  const days = schedule[subjectName];
  if (!days?.length) return schedule;

  return {
    ...schedule,
    [subjectName]: days.map((day) => {
      if (day.day !== dayLabel) return day;
      if (isSpecialScheduleDay(day)) return day;
      return {
        ...day,
        live_session_lcm: true,
        live_session_title: meta?.title || day.live_session_title || 'Live Class Mandatory',
        live_session_key: meta?.sessionKey || day.live_session_key,
        subtopic_ids:
          meta?.subtopicIds && meta.subtopicIds.length > 0
            ? [...meta.subtopicIds]
            : day.subtopic_ids,
      };
    }),
  };
}

/**
 * Apply LCM markers onto the learning day where each plan session's topics complete.
 * Does not add a new calendar day.
 */
export function applyLcmMarkersFromCoursePlans(
  schedule: ScheduleData,
  plans: SubjectLivePlanInput[],
): ScheduleData {
  let next = clearLcmMarkersFromSchedule(schedule);

  for (const plan of plans) {
    const learningDays = (next[plan.subjectName] || []).filter(
      (d) => !isSpecialScheduleDay(d),
    );
    if (!learningDays.length) continue;

    const lcmSessions = sortPlanSessions(plan.sessions).filter(
      (s) => String(s.category || '') === 'mandatory',
    );

    for (const session of lcmSessions) {
      const subtopicIds = collectPlanSessionSubtopicIds(
        session,
        plan.courseTopics,
      );
      if (!subtopicIds.size) continue;

      const lastIdx = lastOverlapLearningIndex(learningDays, subtopicIds);
      if (lastIdx < 0) continue;

      const target = learningDays[lastIdx];
      next = markDayWithLcm(next, plan.subjectName, target.day, {
        title: session.title || 'Live Class Mandatory',
        sessionKey: session.session_key || session.sessionKey,
        subtopicIds: [...subtopicIds],
      });
    }
  }

  return next;
}

const WORKSHOP_TOPICS = new Set([
  ONLINE_WORKSHOP_TOPIC,
  ONSITE_WORKSHOP_TOPIC,
]);

/**
 * Stamp workshop special rows with subtopic ids from plan (LCM uses markers).
 */
export function stampLiveSessionsFromCoursePlans(
  schedule: ScheduleData,
  plans: SubjectLivePlanInput[],
): ScheduleData {
  let next: ScheduleData = { ...schedule };

  for (const plan of plans) {
    const sortedSessions = sortPlanSessions(plan.sessions);

    const queues: Record<string, CoursePlanSession[]> = {
      [ONLINE_WORKSHOP_TOPIC]: [],
      [ONSITE_WORKSHOP_TOPIC]: [],
    };
    for (const session of sortedSessions) {
      const entry = dayTypeEntryForPlanCategory(String(session.category || ''));
      if (!entry || !WORKSHOP_TOPICS.has(entry) || !queues[entry]) continue;
      const count = workshopDayCount(session);
      for (let i = 0; i < count; i += 1) {
        queues[entry].push(session);
      }
    }

    const days = next[plan.subjectName];
    if (!days?.length) continue;

    const cursors: Record<string, number> = {
      [ONLINE_WORKSHOP_TOPIC]: 0,
      [ONSITE_WORKSHOP_TOPIC]: 0,
    };

    next = {
      ...next,
      [plan.subjectName]: days.map((day) => {
        const topic = (day.topic || '').trim();
        const queue = queues[topic];
        if (!queue) return day;
        const idx = cursors[topic] || 0;
        const session = queue[idx];
        cursors[topic] = idx + 1;
        if (!session) return day;
        return {
          ...day,
          subtopic_ids: [
            ...collectPlanSessionSubtopicIds(session, plan.courseTopics),
          ],
        };
      }),
    };
  }

  return next;
}
